import type { FieldMappingDto, ExtractionNode } from '@app/src/types/tabularImport'

type TranslationFn = (descriptor: { id: string }, values?: Record<string, string>) => string

// Duck-type the ExtractionNode since Newtonsoft.Json doesn't emit the System.Text.Json
// polymorphic "type" discriminator. Detect by checking which properties exist.
export function describeExtractionNode(source: ExtractionNode, t: TranslationFn): string {
  if ('columnName' in source) {
    return t({ id: 'import.mapping.fromColumn' }, { column: source.columnName })
  }
  if ('children' in source) {
    const columnNames = source.children.map(input => describeExtractionNode(input, t)).join(' + ')
    return t({ id: 'import.mapping.combined' }, { columns: columnNames, strategy: source.strategy })
  }
  if ('transformType' in source) {
    return t(
      { id: 'import.mapping.transformed' },
      { input: describeExtractionNode(source.input, t), type: source.transformType },
    )
  }
  return 'Unknown mapping'
}

// Collect the source column header(s) an extraction tree reads from. Mirrors the
// duck-typing in describeExtractionNode. A single-column result means the mapping
// can be converted to a category.
export function getSourceColumns(tree: ExtractionNode | null): string[] {
  if (!tree) return []
  if ('columnName' in tree) return [tree.columnName]
  if ('children' in tree) return [...new Set(tree.children.flatMap(getSourceColumns))]
  if ('transformType' in tree) return getSourceColumns(tree.input)
  return []
}

export function sortMappingsByActive(mappings: FieldMappingDto[]): FieldMappingDto[] {
  return [...mappings].sort((a, b) => {
    const aUnmapped = a.extractionTree === null
    const bUnmapped = b.extractionTree === null
    // Unmapped always last
    if (aUnmapped !== bUnmapped) return aUnmapped ? 1 : -1
    // Then applied before unapplied
    if (a.isApplied === b.isApplied) return 0
    return a.isApplied ? -1 : 1
  })
}

export function hasRequiredMappings(mappings: FieldMappingDto[], requiredFields: readonly string[]): boolean {
  const activeFields = new Set(mappings.filter(m => m.isApplied).map(m => m.targetFieldName))
  return requiredFields.every(f => activeFields.has(f))
}

export function getMissingRequiredMappings(mappings: FieldMappingDto[], requiredFields: readonly string[]): string[] {
  const activeFields = new Set(mappings.filter(m => m.isApplied).map(m => m.targetFieldName))
  return requiredFields.filter(f => !activeFields.has(f))
}
